import React, { useState, useEffect, useRef } from "react";
import "../css/Support.css";
import CustomAlert from '../components/CustomAlert';
import leftArrowIcon from "../assets/images/SupportImages/left_arrow.svg";
import faqDownIcon from "../assets/images/SupportImages/faq_down_chev.svg";
import faqUpIcon from "../assets/images/SupportImages/faq_up_chev.svg";
import { supportAPI } from "../services/api";

// Icons for form fields
import subjectIcon from "../assets/images/Formicons/subject-title.svg";
import categoryIcon from "../assets/images/Formicons/chat-category.svg";
import descriptionIcon from "../assets/images/Formicons/description-chat.svg";
import emailIcon from "../assets/images/Formicons/email-icon.svg";
import successTickIcon from "../assets/success_tick.svg";
import sendChatIcon from "../assets/images/Formicons/send-chat.svg";
import ticketsEmptyIcon from "../assets/images/EmptyIcons/not-found-empty.svg";

const Support = () => {
  // Active tab state
  const [activeTab, setActiveTab] = useState('faqs');

  // FAQ States
  const [activeCategory, setActiveCategory] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(0);
  const [categories, setCategories] = useState([]);
  const [faqData, setFaqData] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqError, setFaqError] = useState(null);

  // Raise Ticket States
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: '',
    description: ''
  });
  const [ticketErrors, setTicketErrors] = useState({
    subject: '',
    category: '',
    description: ''
  });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState(null);

  // My Tickets States
  const [ticketFilter, setTicketFilter] = useState('All');
  const [ticketSearch, setTicketSearch] = useState('');
  const [myTickets, setMyTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [statusList, setStatusList] = useState([]);
  const [ticketsPagination, setTicketsPagination] = useState(null);

  // Chat Modal States
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  // Ticket Categories (from API)
  const [ticketCategories, setTicketCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Category dropdown state
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  // CustomAlert state
  const [customAlert, setCustomAlert] = useState({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });

  const showCustomAlert = (type, title, message) => {
    setCustomAlert({ show: true, type, title, message });
  };

  const closeCustomAlert = () => {
    setCustomAlert(prev => ({ ...prev, show: false }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected category name
  const getSelectedCategoryName = () => {
    const selected = ticketCategories.find(cat => cat.Key === ticketForm.category);
    return selected ? selected.Label : null;
  };

  // Fetch FAQs on mount
  useEffect(() => {
    if (activeTab === 'faqs') {
      fetchFAQs();
    }
  }, [activeTab]);

  // Load tickets when My Tickets tab is active
  useEffect(() => {
    if (activeTab === 'my-tickets') {
      fetchTickets();
    }
  }, [activeTab, ticketFilter]);

  // Load categories when Raise Ticket tab is active
  useEffect(() => {
    if (activeTab === 'raise-ticket' && ticketCategories.length === 0) {
      fetchCategories();
    }
  }, [activeTab]);

  // Fetch tickets from API
  const fetchTickets = async (page = 1) => {
    try {
      setTicketsLoading(true);
      setTicketsError(null);

      // Map filter to API status
      const statusMap = {
        'All': null,
        'Open': 2,
        'Pending': 3,
        'Resolved': 4,
        'Closed': 5
      };

      const params = {
        page,
        status: statusMap[ticketFilter]
      };

      const response = await supportAPI.getTickets(params);
      const tickets = response.data.Tickets || response.data.Data?.Tickets || [];

      if (response.data.Status === "Success") {
        setMyTickets(tickets);
        setStatusList(response.data.StatusList || response.data.Data?.StatusList || []);
        setTicketsPagination(response.data.Pagination || response.data.Data?.Pagination || null);
      } else {
        setTicketsError(response.data.Message || "Failed to load tickets.");
      }
    } catch (err) {
      setTicketsError(err.response?.data?.Message || "Failed to load tickets. Please try again.");
    } finally {
      setTicketsLoading(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await supportAPI.getCategories();

      if (response.data.Status === "Success") {
        setTicketCategories(response.data.Categories || response.data.Data?.Categories || []);
      }
    } catch (err) {
      // Error fetching categories
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchFAQs = async (categoryId = null) => {
    try {
      setFaqLoading(true);
      setFaqError(null);
      const params = categoryId ? { category: categoryId } : {};
      const response = await supportAPI.getFAQs(params);

      if (response.data.Status === "Success") {
        setCategories(response.data.Categories || []);
        setFaqData(response.data.Results || []);

        if (!activeCategory && response.data.Categories?.length > 0) {
          setActiveCategory(response.data.Categories[0].Id);
        }
      }
    } catch (err) {
      setFaqError("Failed to load FAQs. Please try again.");
    } finally {
      setFaqLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setOpenFAQ(0);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const getActiveFAQs = () => {
    const category = faqData.find(cat => cat.Id === activeCategory);
    return category?.Faqs || [];
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle ticket form change
  const handleTicketFormChange = (e) => {
    const { name, value } = e.target;
    setTicketForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (ticketErrors[name]) {
      setTicketErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate ticket form
  const validateTicketForm = () => {
    const errors = {};

    if (!ticketForm.subject.trim()) {
      errors.subject = 'Subject title is required';
    }

    if (!ticketForm.category) {
      errors.category = 'Please select a category';
    }

    if (!ticketForm.description.trim()) {
      errors.description = 'Description is required';
    }

    setTicketErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle ticket submit
  const handleTicketSubmit = async (e) => {
    e.preventDefault();

    if (!validateTicketForm()) {
      return;
    }

    setTicketSubmitting(true);

    try {
      const response = await supportAPI.createTicket({
        subject: ticketForm.subject,
        description: ticketForm.description,
        categoryKey: ticketForm.category
      });

      if (response.data.Status === "Success") {
        setCreatedTicketId(response.data.Data?.TicketId || response.data.Data?.FreshdeskTicketId);
        setShowSuccessModal(true);
        setTicketForm({ subject: '', category: '', description: '' });
      }
    } catch (err) {
      showCustomAlert('error', 'Ticket Failed', 'Failed to create ticket. Please try again.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  // Close success modal
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setCreatedTicketId(null);
  };

  // View my tickets from modal
  const handleViewMyTickets = () => {
    setShowSuccessModal(false);
    setActiveTab('my-tickets');
  };

  // Format date from API
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter tickets (search only - status filter is done via API)
  const getFilteredTickets = () => {
    let filtered = myTickets;

    // Filter by search (client-side for quick filtering)
    if (ticketSearch) {
      filtered = filtered.filter(t =>
        String(t.Id).includes(ticketSearch) ||
        (t.Subject && t.Subject.toLowerCase().includes(ticketSearch.toLowerCase()))
      );
    }

    return filtered;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Resolved': return 'status-resolved';
      case 'Open': return 'status-open';
      case 'Pending': return 'status-pending';
      case 'Closed': return 'status-closed';
      default: return '';
    }
  };

  // Open chat modal and fetch ticket details
  const handleOpenChat = async (ticket) => {
    setSelectedTicket(ticket);
    setShowChatModal(true);
    setChatLoading(true);
    setChatMessages([]);

    try {
      const response = await supportAPI.getTicketDetails(ticket.Id);

      if (response.data.Status === "Success") {
        // API returns Ticket (not Data) and Replies at root level
        const ticketData = response.data.Ticket;
        const replies = response.data.Replies || [];

        // Update selected ticket with full details
        setSelectedTicket(prev => ({
          ...prev,
          ...ticketData
        }));

        // Map replies to chat messages format
        // IsAgentReply: true = support message, false = user message
        const mappedMessages = replies.map((reply, index) => ({
          id: index + 1,
          sender: reply.IsAgentReply ? 'Xtrafyl Support' : 'You',
          message: stripHtmlTags(reply.Body),
          time: formatMessageTime(reply.CreatedAt),
          isSupport: reply.IsAgentReply
        }));
        setChatMessages(mappedMessages);
      }
    } catch (err) {
      // Error fetching ticket details
    } finally {
      setChatLoading(false);
    }
  };

  // Format message time
  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Strip HTML tags from message body and extract actual message
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary element to parse HTML and extract text
    const temp = document.createElement('div');
    temp.innerHTML = html;
    let text = temp.textContent || temp.innerText || '';

    // If message has format "AgentName : Prefix,ActualMessage", extract just the actual message
    // Example: "Edmond Pepaj : Hi 9122samrajdhan,Hello shiv test" -> "Hello shiv test"
    if (text.includes(' : ') && text.includes(',')) {
      const afterColon = text.split(' : ').slice(1).join(' : ');
      if (afterColon.includes(',')) {
        text = afterColon.split(',').slice(1).join(',').trim();
      }
    }

    return text;
  };

  // Close chat modal
  const handleCloseChat = () => {
    setShowChatModal(false);
    setSelectedTicket(null);
    setChatMessage('');
    setChatMessages([]);
  };

  // Send chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || sendingReply) return;

    setSendingReply(true);

    try {
      const response = await supportAPI.sendReply(selectedTicket.Id, chatMessage);
      if (response.data.Status === "Success") {
        // Add the new message to chat
        const newMessage = {
          id: chatMessages.length + 1,
          sender: 'You',
          message: chatMessage,
          time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
          isSupport: false
        };
        setChatMessages(prev => [...prev, newMessage]);
        setChatMessage('');
      }
    } catch (err) {
      showCustomAlert('error', 'Message Failed', 'Failed to send message. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="support-page">
      <div className="container-fluid px-4 py-4">

        {/* Top Cards Section */}
        <div className="support-cards-grid">
          <div
            className={`support-card ${activeTab === 'faqs' ? 'card-active' : ''}`}
            onClick={() => handleTabChange('faqs')}
          >
            <div className="support-card-header">
              <h3>FAQ's</h3>
              <img src={leftArrowIcon} alt="arrow" className="card-arrow" />
            </div>
            <p className="support-card-desc">Browse answers to common questions about bookings, payments, and services</p>
          </div>

          <div
            className={`support-card ${activeTab === 'raise-ticket' ? 'card-active' : ''}`}
            onClick={() => handleTabChange('raise-ticket')}
          >
            <div className="support-card-header">
              <h3>Raise a Ticket</h3>
              <img src={leftArrowIcon} alt="arrow" className="card-arrow" />
            </div>
            <p className="support-card-desc">Facing an issue? Submit a request and our team will get back to you soon.</p>
          </div>

          <div
            className={`support-card ${activeTab === 'my-tickets' ? 'card-active' : ''}`}
            onClick={() => handleTabChange('my-tickets')}
          >
            <div className="support-card-header">
              <h3>My Tickets</h3>
              <img src={leftArrowIcon} alt="arrow" className="card-arrow" />
            </div>
            <p className="support-card-desc">View your submitted tickets and track their current status.</p>
          </div>

          <div
            className={`support-card ${activeTab === 'contact-support' ? 'card-active' : ''}`}
            onClick={() => handleTabChange('contact-support')}
          >
            <div className="support-card-header">
              <h3>Contact Support</h3>
              <img src={leftArrowIcon} alt="arrow" className="card-arrow" />
            </div>
            <p className="support-card-desc">Reach our support team directly via call, email, or chat.</p>
          </div>
        </div>

        {/* ==================== FAQ's Tab ==================== */}
        {activeTab === 'faqs' && (
          <>
            {/* Loading State - Shimmer */}
            {faqLoading && (
              <div className="support-content-wrapper">
                <div className="category-sidebar">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div key={item} className="category-tab-shimmer">
                      <div className="shimmer-icon"></div>
                      <div className="shimmer-text"></div>
                    </div>
                  ))}
                </div>
                <div className="support-faq-content">
                  <div className="support-faq-list">
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item} className="faq-item-shimmer">
                        <div className="shimmer-question"></div>
                        <div className="shimmer-answer"></div>
                        <div className="shimmer-answer short"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {faqError && (
              <div className="alert alert-danger" role="alert">
                {faqError}
                <button className="btn btn-link" onClick={() => fetchFAQs()}>
                  Retry
                </button>
              </div>
            )}

            {/* Main Content Section */}
            {!faqLoading && !faqError && (
              <div className="support-content-wrapper">
                <div className="category-sidebar">
                  {categories.map((category) => (
                    <button
                      key={category.Id}
                      className={`category-tab ${activeCategory === category.Id ? "active" : ""}`}
                      onClick={() => handleCategoryChange(category.Id)}
                    >
                      <img src={category.Icon} alt="" className="tab-icon" />
                      <span className="tab-label">{category.Name}</span>
                    </button>
                  ))}
                </div>

                <div className="support-faq-content">
                  <div className="support-faq-list">
                    {getActiveFAQs().length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted">No FAQs available for this category.</p>
                      </div>
                    ) : (
                      getActiveFAQs().map((faq, index) => (
                        <div key={faq.Id} className="support-faq-item">
                          <button
                            className="support-faq-question"
                            onClick={() => toggleFAQ(index)}
                          >
                            <span>{faq.Question}</span>
                            <img
                              src={openFAQ === index ? faqUpIcon : faqDownIcon}
                              alt="toggle"
                              className="support-faq-icon"
                            />
                          </button>
                          <div className={`support-faq-answer ${openFAQ === index ? "open" : ""}`}>
                            <p>{faq.Answer}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ==================== Raise a Ticket Tab ==================== */}
        {activeTab === 'raise-ticket' && (
          <div className="raise-ticket-section">
            <div className="raise-ticket-card">
              <h2 className="raise-ticket-title">Raise a Ticket</h2>

              <form onSubmit={handleTicketSubmit} className="raise-ticket-form" noValidate>
                <div className="support-form-row">
                  {/* Subject Title */}
                  <div className="support-form-group">
                    <label className="support-form-label">Subject Title</label>
                    <div className={`support-input-with-icon ${ticketErrors.subject ? 'support-input-error' : ''}`}>
                      <img src={subjectIcon} alt="" className="support-input-icon" />
                      <input
                        type="text"
                        name="subject"
                        value={ticketForm.subject}
                        onChange={handleTicketFormChange}
                        placeholder="Enter a short title for your issue"
                        className="support-form-input"
                      />
                    </div>
                    {ticketErrors.subject && (
                      <span className="support-error-message">{ticketErrors.subject}</span>
                    )}
                  </div>

                  {/* Category */}
                  <div className="support-form-group" ref={categoryDropdownRef}>
                    <label className="support-form-label">Category</label>
                    <div
                      className={`support-input-field ${ticketErrors.category ? 'support-input-error' : ''}`}
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    >
                      <img src={categoryIcon} alt="" className="support-field-icon" />
                      {getSelectedCategoryName() ? (
                        <span className="support-field-value">{getSelectedCategoryName()}</span>
                      ) : (
                        <span className="support-field-placeholder">Select Category</span>
                      )}
                      <svg
                        className={`support-dropdown-arrow ${showCategoryDropdown ? 'open' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {ticketErrors.category && (
                      <span className="support-error-message">{ticketErrors.category}</span>
                    )}

                    {showCategoryDropdown && (
                      <div className="support-inline-dropdown">
                        {categoriesLoading ? (
                          <div className="support-dropdown-loading">Loading...</div>
                        ) : ticketCategories.length === 0 ? (
                          <div className="support-dropdown-empty">No categories available</div>
                        ) : (
                          ticketCategories.map(cat => (
                            <div
                              key={cat.Key}
                              className={`support-dropdown-option ${ticketForm.category === cat.Key ? 'selected' : ''}`}
                              onClick={() => {
                                setTicketForm(prev => ({ ...prev, category: cat.Key }));
                                setShowCategoryDropdown(false);
                                // Clear category error
                                if (ticketErrors.category) {
                                  setTicketErrors(prev => ({ ...prev, category: '' }));
                                }
                              }}
                            >
                              <span className="support-radio-custom"></span>
                              <span className="support-option-label">{cat.Label}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="support-form-group support-full-width">
                  <label className="support-form-label">Description</label>
                  <div className={`support-input-with-icon support-textarea-wrapper ${ticketErrors.description ? 'support-input-error' : ''}`}>
                    <img src={descriptionIcon} alt="" className="support-input-icon support-textarea-icon" />
                    <textarea
                      name="description"
                      value={ticketForm.description}
                      onChange={handleTicketFormChange}
                      placeholder="Describe your issue in detail"
                      className="support-form-textarea"
                      rows={5}
                    />
                  </div>
                  {ticketErrors.description && (
                    <span className="support-error-message">{ticketErrors.description}</span>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="submit-ticket-btn"
                  disabled={ticketSubmitting}
                >
                  {ticketSubmitting ? 'Submitting...' : 'SUBMIT TICKET'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ==================== My Tickets Tab ==================== */}
        {activeTab === 'my-tickets' && (
          <div className="my-tickets-section">
            {/* Filter Tabs & Search */}
            <div className="ticket-filter-row">
              <div className="ticket-filter-tabs">
                {['All', 'Open', 'Pending', 'Resolved', 'Closed'].map(filter => (
                  <button
                    key={filter}
                    className={`ticket-filter-tab ${ticketFilter === filter ? 'active' : ''}`}
                    onClick={() => setTicketFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>

              <div className="ticket-search-box">
                <input
                  type="text"
                  placeholder="Search by Ticket ID"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  className="ticket-search-input"
                />
                <svg className="ticket-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
              </div>
            </div>

            {/* Tickets Table Header */}
            <div className="ticket-table-header-row">
              <div className="ticket-header-cell ticket-header-id">Ticket ID</div>
              <div className="ticket-header-cell ticket-header-category">Category</div>
              <div className="ticket-header-cell ticket-header-date">Date Raised</div>
              <div className="ticket-header-cell ticket-header-status">Status</div>
              <div className="ticket-header-cell ticket-header-action">Action</div>
            </div>

            {/* Tickets List */}
            <div className="ticket-list">
              {ticketsLoading ? (
                <div className="ticket-loading-state">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="ticket-item-shimmer">
                      <div className="shimmer-row"></div>
                      <div className="shimmer-meta"></div>
                    </div>
                  ))}
                </div>
              ) : ticketsError ? (
                <div className="ticket-error-state">
                  <p>{ticketsError}</p>
                  <button className="ticket-retry-btn" onClick={() => fetchTickets()}>
                    Retry
                  </button>
                </div>
              ) : getFilteredTickets().length === 0 ? (
                <div className="ticket-empty-state">
                  <img src={ticketsEmptyIcon} alt="No Tickets" className="ticket-empty-icon" />
                  <h3 className="ticket-empty-title">No Tickets Found</h3>
                  <p className="ticket-empty-text">
                    {ticketSearch
                      ? "No tickets match your search. Try a different keyword."
                      : ticketFilter !== 'All'
                        ? `There are no ${ticketFilter.toLowerCase()} tickets at the moment.`
                        : "You haven't raised any tickets yet. Click 'Raise a Ticket' to submit your first request."
                    }
                  </p>
                  {ticketFilter === 'All' && !ticketSearch && (
                    <button className="ticket-empty-btn" onClick={() => setActiveTab('raise-ticket')}>
                      RAISE A TICKET
                    </button>
                  )}
                </div>
              ) : (
                getFilteredTickets().map((ticket) => (
                  <div key={ticket.Id} className="ticket-item-card">
                    <div className="ticket-item-row">
                      <div className="ticket-item-col ticket-col-id">#{ticket.Id}</div>
                      <div className="ticket-item-col ticket-col-category">{ticket.CategoryDisplay || '-'}</div>
                      <div className="ticket-item-col ticket-col-date">{formatDate(ticket.CreatedAt)}</div>
                      <div className="ticket-item-col ticket-col-status">
                        <span className={`ticket-status-badge ${getStatusBadgeClass(ticket.StatusDisplay)}`}>
                          {ticket.StatusDisplay || '-'}
                        </span>
                      </div>
                      <div className="ticket-item-col ticket-col-action">
                        <button
                          className="ticket-open-chat-btn"
                          onClick={() => handleOpenChat(ticket)}
                        >
                          Open Chat
                        </button>
                      </div>
                    </div>
                    <div className="ticket-item-meta">
                      <div className="ticket-meta-left">
                        <div className="ticket-meta-row">
                          <span className="ticket-meta-label">Subject:</span>
                          <span className="ticket-meta-value">{ticket.Subject || '-'}</span>
                        </div>
                      </div>
                      <div className="ticket-meta-right">
                        <span className="ticket-meta-label">Last Updated:</span>
                        <span className="ticket-meta-value">{formatDate(ticket.UpdatedAt)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ==================== Contact Support Tab ==================== */}
        {activeTab === 'contact-support' && (
          <div className="contact-support-section">
            <div className="contact-card">
              <div className="contact-header">
                <img src={emailIcon} alt="Email" className="contact-email-icon" />
                <span className="contact-label">Email Address</span>
              </div>
              <a href="mailto:support@xtrafyl.com" className="contact-email">support@xtrafyl.com</a>
              <p className="contact-note">We usually respond within 24 hours</p>
            </div>
          </div>
        )}

      </div>

      {/* ==================== Success Modal ==================== */}
      {showSuccessModal && (
        <div className="ticket-modal-overlay" onClick={closeSuccessModal}>
          <div className="ticket-success-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ticket-close-modal-btn" onClick={closeSuccessModal}>
              ✕
            </button>

            <div className="ticket-success-icon">
              <img src={successTickIcon} alt="Success" className="ticket-success-img" />
            </div>

            <h2 className="ticket-success-title">Ticket Created!</h2>
            <p className="ticket-success-message">
              Your ticket ID is <span className="ticket-id">#{createdTicketId}</span>. You can track its progress in My Tickets.
            </p>

            <button className="ticket-view-btn" onClick={handleViewMyTickets}>
              VIEW MY TICKETS
            </button>
          </div>
        </div>
      )}

      {/* ==================== Chat Modal ==================== */}
      {showChatModal && selectedTicket && (
        <div className="ticket-chat-modal">
          {/* Chat Header */}
          <div className="ticket-chat-header">
            <div className="ticket-chat-header-left">
              <span className="ticket-chat-id">Ticket ID #{selectedTicket.Id}</span>
              <span className={`ticket-chat-status ${getStatusBadgeClass(selectedTicket.StatusDisplay)}`}>
                {selectedTicket.StatusDisplay || '-'}
              </span>
            </div>
            <button className="ticket-chat-close-btn" onClick={handleCloseChat}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Ticket Info */}
          <div className="ticket-chat-info">
            <h3 className="ticket-chat-subject">{selectedTicket.Subject || '-'}</h3>
            <div className="ticket-chat-meta">
              <span>Category: <strong>{selectedTicket.CategoryDisplay || '-'}</strong></span>
            </div>
            <div className="ticket-chat-meta">
              <span>Description: <strong>{selectedTicket.Description || '-'}</strong></span>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="ticket-chat-messages">
            {chatLoading ? (
              <div className="ticket-chat-loading">
                <span>Loading messages...</span>
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="ticket-chat-empty">
                <span>No messages yet. Start the conversation!</span>
              </div>
            ) : (
              chatMessages.map((msg) => (
                <div key={msg.id} className={`ticket-chat-message ${msg.isSupport ? 'support' : 'user'}`}>
                  {/* Support: header above bubble, User: header below bubble */}
                  {msg.isSupport && (
                    <div className="ticket-chat-message-header">
                      <span className="ticket-chat-sender">{msg.sender}</span>
                      <span className="ticket-chat-time">{msg.time}</span>
                    </div>
                  )}
                  <div className="ticket-chat-bubble">
                    {msg.message && msg.message.split('\n').map((line, i) => (
                      <span key={i}>{line}<br/></span>
                    ))}
                  </div>
                  {!msg.isSupport && (
                    <div className="ticket-chat-message-header">
                      <span className="ticket-chat-sender">{msg.sender}</span>
                      <span className="ticket-chat-time">{msg.time}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Chat Input */}
          <form className="ticket-chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Type your Message"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              className="ticket-chat-input"
              disabled={sendingReply}
            />
            <button type="submit" className="ticket-chat-send-btn" disabled={sendingReply}>
              <img src={sendChatIcon} alt="Send" className="ticket-chat-send-icon" />
            </button>
          </form>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={customAlert.show}
        onClose={closeCustomAlert}
        type={customAlert.type}
        title={customAlert.title}
        message={customAlert.message}
      />
    </div>
  );
};

export default Support;
